import { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, Switch, Space,
  notification, Popconfirm, Tag, Tooltip, Row, Col, Image,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined,
  EyeOutlined, PictureOutlined, PlayCircleOutlined, FileOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const MEDIA_TYPES = [
  { value: 'image',    label: 'Image / Photo',    color: 'blue'   },
  { value: 'video',    label: 'Video',             color: 'red'    },
  { value: 'document', label: 'Document / PDF',   color: 'orange' },
  { value: 'album',    label: 'Album (Folder)',    color: 'purple' },
  { value: 'folder',   label: 'Drive Folder',     color: 'green'  },
];

// Extract Google Drive file ID from various link formats
function parseDriveId(link) {
  if (!link) return null;
  const m =
    link.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) ||
    link.match(/id=([a-zA-Z0-9_-]+)/) ||
    link.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  return m ? m[1] : link.trim();
}

// Generate embeddable thumbnail URL from Drive ID
function driveThumbnail(link) {
  const id = parseDriveId(link);
  if (!id) return null;
  return `https://drive.google.com/thumbnail?id=${id}&sz=w200`;
}

// Generate embeddable preview URL
function driveEmbed(link, type) {
  const id = parseDriveId(link);
  if (!id) return null;
  if (type === 'image')    return `https://drive.google.com/uc?export=view&id=${id}`;
  if (type === 'video')    return `https://drive.google.com/file/d/${id}/preview`;
  if (type === 'document') return `https://drive.google.com/file/d/${id}/preview`;
  if (type === 'album' || type === 'folder')
    return `https://drive.google.com/embeddedfolderview?id=${id}#grid`;
  return `https://drive.google.com/file/d/${id}/preview`;
}

const CATEGORIES = ['event','sammelan','darbar','daily','nature','festival','samaj','program','sports','other'];

export default function GalleryAdmin() {
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [modal,     setModal]     = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [preview,   setPreview]   = useState(null); // item to preview
  const [catFilter, setCatFilter] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async (pg = 1, cat = catFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 20 });
      if (cat) params.set('category', cat);
      const r = await axios.get(`${baseUrl}/admin/gallery?${params}`, { headers: auth() });
      if (r.data.status) { setData(r.data.result || []); setTotal(r.data.total || 0); setPage(pg); }
    } catch { notification.error({ message: 'Load failed', placement: 'topRight' }); }
    finally  { setLoading(false); }
  }, [catFilter]);

  useEffect(() => { load(1); }, [load]);

  const openNew = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ media_type: 'image', is_active: true, sort_order: 0 });
    setModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      title_hi:       row.title_hi,
      title_en:       row.title_en   || '',
      description_hi: row.description_hi || '',
      description_en: row.description_en || '',
      drive_link:     row.drive_link,
      thumbnail_url:  row.thumbnail_url || '',
      media_type:     row.media_type,
      category:       row.category || undefined,
      sort_order:     row.sort_order || 0,
      is_active:      !!row.is_active,
    });
    setModal(true);
  };

  const save = async () => {
    let vals;
    try { vals = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const url    = editing ? `${baseUrl}/admin/gallery/${editing.id}` : `${baseUrl}/admin/gallery`;
      const method = editing ? 'patch' : 'post';
      const r = await axios[method](url, vals, { headers: auth() });
      if (r.data.status) {
        notification.success({ message: editing ? 'Updated!' : 'Added!', placement: 'topRight' });
        setModal(false); load(1);
      } else {
        notification.error({ message: r.data.message, placement: 'topRight' });
      }
    } catch (err) {
      notification.error({ message: err.message, placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${baseUrl}/admin/gallery/${id}`, { headers: auth() });
      notification.success({ message: 'Deleted.', placement: 'topRight' });
      load(page);
    } catch { notification.error({ message: 'Delete failed', placement: 'topRight' }); }
  };

  const toggleActive = async (id, checked) => {
    try {
      await axios.patch(`${baseUrl}/admin/gallery/${id}`, { is_active: checked }, { headers: auth() });
      setData(prev => prev.map(r => r.id === id ? { ...r, is_active: checked ? 1 : 0 } : r));
    } catch { notification.error({ message: 'Toggle failed', placement: 'topRight' }); }
  };

  const typeTag = (type) => {
    const t = MEDIA_TYPES.find(m => m.value === type);
    if (!t) return <Tag>{type}</Tag>;
    const icons = { image: <PictureOutlined />, video: <PlayCircleOutlined />, document: <FileOutlined />, album: <PictureOutlined />, folder: <LinkOutlined /> };
    return <Tag color={t.color} icon={icons[type]}>{t.label}</Tag>;
  };

  const columns = [
    {
      title: 'Thumbnail',
      width: 80,
      render: (_, r) => {
        // Only try Drive thumbnail for individual files (image/video), not folders/albums
        const canThumb = r.thumbnail_url || (r.media_type === 'image' || r.media_type === 'video');
        const thumb = r.thumbnail_url || (canThumb && !r.drive_link?.includes('/folders/') ? driveThumbnail(r.drive_link) : null);
        const iconMap = { folder: '📁', album: '🗂', document: '📄', video: '🎬', image: '🖼' };
        if (!thumb) {
          return (
            <div style={{ width: 60, height: 45, background: '#f5f5f5', borderRadius: 4,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <span style={{ fontSize: 20 }}>{iconMap[r.media_type] || '📎'}</span>
              <span style={{ fontSize: 9, color: '#6b7280', fontWeight: 600 }}>{r.media_type?.toUpperCase()}</span>
            </div>
          );
        }
        return <Image src={thumb} width={60} height={45} style={{ objectFit: 'cover', borderRadius: 4 }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==" />;
      },
    },
    {
      title: 'Title',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.title_hi}</div>
          {r.title_en && <div style={{ fontSize: 11, color: '#6b7280' }}>{r.title_en}</div>}
          {r.category && <Tag style={{ marginTop: 3, fontSize: 10 }}>{r.category}</Tag>}
        </div>
      ),
    },
    { title: 'Type', width: 140, dataIndex: 'media_type', render: typeTag },
    {
      title: 'Drive Link',
      dataIndex: 'drive_link',
      render: v => (
        <a href={v} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <LinkOutlined /> {v}
        </a>
      ),
    },
    {
      title: 'Sort',
      dataIndex: 'sort_order',
      width: 60,
      render: v => <span style={{ color: '#6b7280', fontSize: 12 }}>{v}</span>,
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      width: 70,
      render: (v, r) => <Switch size="small" checked={!!v} onChange={c => toggleActive(r.id, c)} />,
    },
    {
      title: 'Actions',
      width: 120,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Preview">
            <Button size="small" icon={<EyeOutlined />} onClick={() => setPreview(r)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          </Tooltip>
          <Popconfirm title="Delete this item?" onConfirm={() => remove(r.id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        bordered={false}
        bodyStyle={{ padding: 16 }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Gallery Management</span>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                Add Google Drive links — images, videos, documents, or albums. No files stored on server.
              </div>
            </div>
            <Space>
              <Select
                allowClear
                placeholder="Filter by category"
                style={{ width: 160 }}
                value={catFilter}
                onChange={v => { setCatFilter(v || null); load(1, v || null); }}
              >
                {CATEGORIES.map(c => <Select.Option key={c} value={c}>{c}</Select.Option>)}
              </Select>
              <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>Add Item</Button>
            </Space>
          </div>
        }
      >
        <Table
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="middle"
          pagination={{ current: page, total, pageSize: 20, onChange: pg => load(pg) }}
          locale={{ emptyText: 'No gallery items yet. Click "Add Item" to create one.' }}
        />
      </Card>

      {/* ── Add / Edit Modal ── */}
      <Modal
        open={modal}
        title={editing ? 'Edit Gallery Item' : 'Add Gallery Item'}
        onCancel={() => setModal(false)}
        onOk={save}
        okText={editing ? 'Update' : 'Add'}
        confirmLoading={saving}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" size="small" style={{ marginTop: 12 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="title_hi" label="Title (Hindi)" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="जैसे: समागम 2024 की झलकियाँ" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="title_en" label="Title (English)">
                <Input placeholder="e.g. Samagam 2024 Highlights" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="drive_link" label="Google Drive Link" rules={[{ required: true, message: 'Required' }]}
            help="Paste the sharing link from Google Drive (File, Folder, Album). Make sure 'Anyone with link can view' is enabled.">
            <Input placeholder="https://drive.google.com/file/d/... or drive.google.com/drive/folders/..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="media_type" label="Type">
                <Select>
                  {MEDIA_TYPES.map(t => <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="category" label="Category">
                <Select allowClear placeholder="Select category">
                  {CATEGORIES.map(c => <Select.Option key={c} value={c}>{c}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="thumbnail_url" label="Custom Thumbnail URL (optional)"
            help="Leave blank to auto-generate from Drive. Only needed if Drive thumbnail doesn't work.">
            <Input placeholder="https://..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="description_hi" label="Description (Hindi)">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="description_en" label="Description (English)">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sort_order" label="Sort Order (lower = first)">
                <Input type="number" min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Visible to Members" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ── Preview Modal ── */}
      <Modal
        open={!!preview}
        title={preview?.title_hi}
        onCancel={() => setPreview(null)}
        footer={[
          <Button key="open" type="primary" href={preview?.drive_link} target="_blank" rel="noopener noreferrer">
            <LinkOutlined /> Open in Drive
          </Button>,
          <Button key="close" onClick={() => setPreview(null)}>Close</Button>,
        ]}
        width={700}
        destroyOnClose
      >
        {preview && (() => {
          const embedUrl = driveEmbed(preview.drive_link, preview.media_type);
          if (preview.media_type === 'image') {
            return (
              <div style={{ textAlign: 'center' }}>
                <img src={embedUrl} alt={preview.title_hi} style={{ maxWidth: '100%', maxHeight: 450, borderRadius: 8 }} />
              </div>
            );
          }
          return (
            <iframe
              src={embedUrl}
              width="100%"
              height="420"
              allow="autoplay"
              style={{ border: 'none', borderRadius: 8 }}
              title={preview.title_hi}
            />
          );
        })()}
        {preview?.description_hi && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#374151' }}>{preview.description_hi}</div>
        )}
      </Modal>
    </div>
  );
}
